/**
 * frontend/src/components/LinkedPlaylists.jsx
 * SoundDAO Sprint 6A — the "Linked" playlist section (external Spotify / Apple Music /
 * YouTube / SoundCloud / Tidal URL → live playlist_share amount once a day).
 *
 * MOVED here unchanged from PlayerDashboard (Sprint 4 M8 / Prompt 1.5): same endpoints
 * (/api/playlists/submit, /api/playlists/user/:id, /api/rewards/latest), same cooldown,
 * same markup and dashboard CSS classes, same analytics event. The only additions are the
 * data loads the dashboard used to do for it (cooldown, recent submissions, creator roster).
 */

import React, { useState, useEffect, useRef } from 'react';
import { supabase } from '../lib/supabaseClient';
import { authFetch } from '../lib/authFetch';
import { useRates, sound } from '../lib/rates';
import posthog from 'posthog-js';
import '../dashboards/Dashboard.css';

const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:5001';

// ── Playlist embed URL resolver ────────────────────────────────────────────────
function getEmbedUrl(url, platform) {
  try {
    if (platform === 'spotify') {
      const match = url.match(/playlist\/([a-zA-Z0-9]+)/);
      if (match) return `https://open.spotify.com/embed/playlist/${match[1]}?utm_source=generator`;
    }
    if (platform === 'soundcloud') {
      return `https://w.soundcloud.com/player/?url=${encodeURIComponent(url)}&auto_play=false&hide_related=true&show_comments=false&show_user=true&show_reposts=false&visual=false&color=%23f97316`;
    }
    if (platform === 'youtube') {
      const match = url.match(/list=([a-zA-Z0-9_-]+)/);
      if (match) return `https://www.youtube.com/embed/videoseries?list=${match[1]}`;
    }
    return null;
  } catch {
    return null;
  }
}

// ── Playlist name extractor ────────────────────────────────────────────────────
function getPlaylistName(url) {
  try {
    const urlObj = new URL(url);
    const parts = urlObj.pathname.split('/').filter(Boolean);
    let name = parts[parts.length - 2] || parts[parts.length - 1] || '';
    name = decodeURIComponent(name).replace(/-/g, ' ');
    if (!name || name.length < 3) return urlObj.hostname.replace('www.', '');
    return name.length > 40 ? name.substring(0, 40) + '…' : name;
  } catch {
    return url.length > 40 ? url.substring(0, 40) + '…' : url;
  }
}

export default function LinkedPlaylists({ userId }) {
  const rates = useRates();
  const user_id = userId;

  // ── Playlist state (moved from PlayerDashboard) ─────────────────────────────
  const [playlistUrl,           setPlaylistUrl]           = useState('');
  const [playlistCreatorId,     setPlaylistCreatorId]     = useState('');
  const [playlistSubmitting,    setPlaylistSubmitting]    = useState(false);
  const [playlistMsg,           setPlaylistMsg]           = useState(null);
  const [recentPlaylists,       setRecentPlaylists]       = useState([]);
  const [creatorRoster,         setCreatorRoster]         = useState([]);
  const [playlistCooldownUntil, setPlaylistCooldownUntil] = useState(null);
  const [playlistCooldownLabel, setPlaylistCooldownLabel] = useState('');

  const playlistMsgRef     = useRef(null);
  const playlistSectionRef = useRef(null);

  // ── Load: cooldown, recent submissions, creator roster (same queries as the dashboard) ──
  useEffect(() => {
    if (!user_id) return;
    let alive = true;
    (async () => {
      // Creator roster for the optional "tag a creator" dropdown: recruited creators
      // with a complete profile, from the rewards ledger (recruit identity lives in
      // rewards.metadata.target_id).
      try {
        const { data: recruitRewards } = await supabase
          .from('rewards')
          .select('metadata')
          .eq('user_id', user_id)
          .eq('action_type', 'recruit_creator');
        const recruitTargetIds = (recruitRewards || [])
          .map(r => r.metadata?.target_id)
          .filter(Boolean);
        if (recruitTargetIds.length > 0) {
          const { data: profiles } = await supabase
            .from('creators')
            .select('user_id, artist_name')
            .in('user_id', recruitTargetIds);
          if (alive) setCreatorRoster((profiles || []).filter(p => p.artist_name));
        }
      } catch { /* non-fatal */ }

      // Playlist cooldown
      try {
        const latestRes = await authFetch(
          `${API_BASE}/api/rewards/latest?user_id=${user_id}&action=playlist_share`
        );
        if (latestRes.ok) {
          const { reward } = await latestRes.json();
          if (!alive) return;
          if (reward?.created_at) {
            const expiry = new Date(new Date(reward.created_at).getTime() + 24 * 60 * 60 * 1000);
            setPlaylistCooldownUntil(expiry > new Date() ? expiry : null);
          } else {
            setPlaylistCooldownUntil(null);
          }
        }
      } catch { /* non-fatal */ }

      // Recent playlists
      try {
        const plRes = await authFetch(`${API_BASE}/api/playlists/user/${user_id}?limit=10`);
        if (plRes.ok) {
          const pd = await plRes.json();
          if (alive) setRecentPlaylists(pd.playlists || []);
        }
      } catch { /* non-fatal */ }
    })();
    return () => { alive = false; };
  }, [user_id]);

  // ── Playlist cooldown countdown ────────────────────────────────────────────────
  useEffect(() => {
    if (!playlistCooldownUntil) { setPlaylistCooldownLabel(''); return; }
    const tick = () => {
      const diff = Math.max(0, playlistCooldownUntil - Date.now());
      if (diff === 0) { setPlaylistCooldownUntil(null); setPlaylistCooldownLabel(''); return; }
      const h = Math.floor(diff / 3600000);
      const m = Math.floor((diff % 3600000) / 60000);
      const s = Math.floor((diff % 60000) / 1000);
      setPlaylistCooldownLabel(
        h > 0 ? `${h}h ${m}m` : m > 0 ? `${m}m ${s}s` : `${s}s`
      );
    };
    tick();
    const id = setInterval(tick, 1000);
    return () => clearInterval(id);
  }, [playlistCooldownUntil]);

  // ── Playlist submit ────────────────────────────────────────────────────────────
  const handlePlaylistSubmit = async () => {
    if (!playlistUrl.trim() || playlistSubmitting) return;
    setPlaylistSubmitting(true);
    setPlaylistMsg(null);
    try {
      // Prompt 1.5: the submitter is the token holder.
      const body = { url: playlistUrl.trim() };
      if (playlistCreatorId) body.creator_id = playlistCreatorId;

      const res = await authFetch(`${API_BASE}/api/playlists/submit`, {
        method:  'POST',
        headers: { 'Content-Type': 'application/json' },
        body:    JSON.stringify(body),
      });
      const data = await res.json();

      if (!res.ok) {
        setPlaylistMsg({ type: 'error', text: data.error || 'Submission failed' });
        return;
      }

      if (data.already_awarded) {
        setPlaylistMsg({ type: 'cooldown', text: 'Playlist saved! Reward resets in 24 h.' });
      } else {
        setPlaylistMsg({ type: 'success', text: `+${data.sound_awarded} SOUND earned!` });
        setPlaylistCooldownUntil(new Date(Date.now() + 24 * 60 * 60 * 1000));
      }

      setPlaylistUrl('');
      setPlaylistCreatorId('');
      // Scroll success message into view (mobile: may be hidden behind keyboard)
      setTimeout(() => playlistMsgRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' }), 100);
      try { posthog.capture('playlist_submitted', { sound: data.sound_awarded, already: !!data.already_awarded }); } catch (_) { /* analytics non-fatal */ }

      const plRes = await authFetch(`${API_BASE}/api/playlists/user/${user_id}?limit=10`);
      if (plRes.ok) {
        const pd = await plRes.json();
        setRecentPlaylists(pd.playlists || []);
      }
    } catch (err) {
      console.error('[LinkedPlaylists] playlist submit error:', err);
      setPlaylistMsg({ type: 'error', text: 'Something went wrong. Please try again.' });
    } finally {
      setPlaylistSubmitting(false);
    }
  };

  return (
    <>
      {/* ══════ Playlist submission ══════ */}
      <div ref={playlistSectionRef} className="dashboard-section dashboard-playlist-section">
        <h2 className="dashboard-section-title">🎵 Share a Playlist</h2>
        <p className="dashboard-playlist-desc">
          Share a playlist featuring your favourite creators. Earn {sound(rates, 'playlist_share')} once per day.
        </p>

        <div className={`dashboard-reward-badge ${playlistCooldownUntil ? 'dashboard-reward-badge--cooldown' : 'dashboard-reward-badge--active'} dashboard-reward-badge--playlist`}>
          {playlistCooldownUntil
            ? `⏳ Next reward in ${playlistCooldownLabel || '…'}`
            : `🎧 Submit a playlist — earn ${sound(rates, 'playlist_share')}`}
        </div>

        <input
          className="dashboard-playlist-input"
          type="url"
          inputMode="url"
          autoCapitalize="none"
          autoCorrect="off"
          placeholder="Paste Spotify, Apple Music, YouTube, SoundCloud… link"
          value={playlistUrl}
          onChange={e => setPlaylistUrl(e.target.value)}
          onKeyDown={e => e.key === 'Enter' && handlePlaylistSubmit()}
          disabled={playlistSubmitting}
        />

        {creatorRoster.length > 0 && (
          <select
            className="dashboard-playlist-creator-select"
            value={playlistCreatorId}
            onChange={e => setPlaylistCreatorId(e.target.value)}
          >
            <option value="">— Tag a creator (optional) —</option>
            {creatorRoster.map(c => (
              <option key={c.user_id} value={c.user_id}>
                {c.artist_name}
              </option>
            ))}
          </select>
        )}

        <button
          className="dashboard-playlist-submit-btn"
          onClick={handlePlaylistSubmit}
          disabled={!playlistUrl.trim() || playlistSubmitting}
        >
          {playlistSubmitting ? 'Submitting…' : 'Submit Playlist'}
        </button>

        {playlistMsg && (
          <div ref={playlistMsgRef} className={`dashboard-playlist-msg dashboard-playlist-msg--${playlistMsg.type}`}>
            {playlistMsg.text}
          </div>
        )}

        {recentPlaylists.length > 0 && (
          <div className="dashboard-playlist-recent">
            <p className="dashboard-playlist-recent-label">Recent submissions</p>
            {recentPlaylists.map(pl => {
              const plUrl    = pl.url || pl.playlist_url;
              const embedUrl = getEmbedUrl(plUrl, pl.platform);
              const embedH   = pl.platform === 'soundcloud' ? 120
                             : pl.platform === 'youtube'    ? 200
                             :                                152;
              return (
                <div key={pl.id} className="dashboard-playlist-recent-item">
                  <div className="dashboard-playlist-recent-header">
                    <span className="dashboard-playlist-platform-icon">
                      {pl.platform === 'spotify'     ? '🟢' :
                       pl.platform === 'apple_music' ? '🎵' :
                       pl.platform === 'youtube'     ? '▶️' :
                       pl.platform === 'soundcloud'  ? '🔶' :
                       pl.platform === 'tidal'       ? '🌊' : '🔗'}
                    </span>
                    <a
                      className="dashboard-playlist-recent-url"
                      href={plUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      <span className="playlist-name">{getPlaylistName(plUrl || '')}</span>
                    </a>
                    {pl.artist_name && (
                      <span className="dashboard-playlist-recent-creator">{pl.artist_name}</span>
                    )}
                  </div>
                  {embedUrl ? (
                    <iframe
                      src={embedUrl}
                      width="100%"
                      height={embedH}
                      frameBorder="no"
                      scrolling="no"
                      allow="autoplay; clipboard-write; encrypted-media; fullscreen; picture-in-picture"
                      loading="lazy"
                      title={`Playlist — ${pl.platform}`}
                      className="dashboard-playlist-embed"
                      style={{ marginTop: 8, borderRadius: 12, display: 'block' }}
                    />
                  ) : plUrl && plUrl.includes('music.apple.com') ? (
                    <a
                      href={plUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="music-link-card music-link-card--apple"
                    >
                      <div className="music-link-card-icon">🎵</div>
                      <div className="music-link-card-text">
                        <div className="music-link-card-label">Apple Music</div>
                        <div className="music-link-card-url">
                          {plUrl.replace('https://music.apple.com/', '').slice(0, 40)}…
                        </div>
                      </div>
                      <div className="music-link-card-arrow">→</div>
                    </a>
                  ) : null}
                </div>
              );
            })}
          </div>
        )}
      </div>
    </>
  );
}
